import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** "45 s", "2 min", "1 h 5 min" — waits and durations as people say them. */
export function formatWait(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const minutes = Math.round(s / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/** Media timecode: 0:07, 1:32. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "";
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function formatClock(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Today, ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
  return `${date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, ${time}`;
}

export function modelLabel(model: string | null | undefined): string {
  switch (model) {
    case "qwen3.8-27b":
      return "chat model";
    case "z-image":
    case "qwen-image-2.1":
    case "qwen-image-2.1-turbo":
    case "flux2-klein-9b":
    case "flux2-klein-base-9b":
    case "flux2-klein-base-fast-9b":
    case "flux3-klein":
      return "image model";
    case "minimax-h3":
    case "h3-fl2va":
    case "h3-ref2va":
      return "video model";
    case "minimax-music3":
      return "music model";
    case "qwen3-tts":
    case "qwen3-tts-custom":
    case "qwen3-tts-design":
      return "speech model";
    case "wonder-3.5":
    case "denoise-max":
    case "precise-2.6":
    case "nyx-3":
    case "hyperion-2.5":
      return "enhancement model";
    default:
      return model ?? "model";
  }
}
